<script setup lang="ts">
/**
 * Invoices — the documents the game issued.
 *
 * ── Formerly "Billing" ────────────────────────────────────────────────────────
 *
 * This pane used to carry KYC-waiting, Withdrawals and Payments alongside
 * Invoices. Those three are gone from here — not gone from the API, just no
 * longer grouped under this rail entry. `GET /admin/kyc/users`,
 * `GET /admin/withdrawals` and `GET /admin/payments` stay live and untouched;
 * nothing else in the app called this pane's sections, so nothing else needed
 * to change. If those need a home again, they are still ordinary endpoints —
 * this file just stopped being where they render.
 *
 * ── The PDF link is a plain `<a>`, not a fetch ───────────────────────────────
 *
 * Same reasoning as the self-service billing pane's own download link
 * (`profile/BillingPane.vue`): the browser navigating to the URL directly is
 * what makes `Content-Disposition: attachment` work, and the admin session
 * cookie rides along on an ordinary navigation the same way it rides along on
 * `request()`. Fetching it into a blob would buy nothing and would have to
 * fight the viewer's own sandbox rules to save it.
 */
import { ref, watch } from 'vue';

import { adminApi } from './admin-api';
import GenerateInvoiceModal from './GenerateInvoiceModal.vue';
import { money, usePane, when } from './pane';

const invoicePage = ref(1);
const invoices = usePane(() => adminApi.invoices({ page: invoicePage.value }));
watch(invoicePage, () => void invoices.reload());

const showGenerate = ref(false);
function onInvoiceGenerated(): void {
  showGenerate.value = false;
  invoicePage.value = 1;
  void invoices.reload();
}

const invoicePages = () => {
  const d = invoices.data.value;
  return d ? Math.max(1, Math.ceil(d.total / d.pageSize)) : 1;
};

/** Matches `InvoiceService.sendInvoiceEmail`'s own mapping, server-side. */
function typeLabel(type: string): string {
  return type === 'IN' ? 'Deposit' : 'Withdrawal';
}

const API_BASE = import.meta.env.VITE_API_URL || '';

/** See the header comment — a plain URL, not a fetch. */
function pdfUrl(number: string): string {
  return `${API_BASE}/api/admin/invoices/${encodeURIComponent(number)}/download`;
}
</script>

<template>
  <h1>Invoices</h1>
  <p class="lede">The documents the game has issued, and their PDFs.</p>

  <section>
    <h2>Invoices</h2>
    <div class="toolbar">
      <button class="accent" @click="showGenerate = true">+ Generate invoice</button>
    </div>
    <p v-if="invoices.error.value" class="bad">{{ invoices.error.value }}</p>
    <div class="scroller">
      <table data-test="admin-invoices-table">
        <thead>
          <tr>
            <th>Number</th><th>Type</th><th>Email</th><th>Amount</th>
            <th>Status</th><th>Date</th><th>PDF</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in invoices.data.value?.items ?? []" :key="row.id">
            <td>{{ row.number }}</td>
            <td>{{ typeLabel(row.type) }}</td>
            <td>{{ row.user?.email ?? '—' }}</td>
            <td>{{ money(row.totalCents, row.currency) }}</td>
            <td>
              <span class="chip" :class="row.status === 'PAID' ? 'ok' : 'warn'">{{ row.status }}</span>
            </td>
            <td>{{ when(row.createdAt) }}</td>
            <td>
              <a
                :href="pdfUrl(row.number)"
                target="_blank"
                rel="noopener"
                :data-test="`admin-invoice-pdf-${row.number}`"
                :title="`Download ${row.number} as a PDF`"
              >
                Download
              </a>
            </td>
          </tr>
          <tr v-if="!invoices.loading.value && (invoices.data.value?.items.length ?? 0) === 0">
            <td colspan="7" class="empty">No invoices yet.</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="pager">
      <button :disabled="invoicePage <= 1 || invoices.loading.value" @click="invoicePage -= 1">Newer</button>
      <span>Page {{ invoicePage }} of {{ invoicePages() }}</span>
      <button
        :disabled="invoicePage >= invoicePages() || invoices.loading.value"
        @click="invoicePage += 1"
      >Older</button>
    </div>
  </section>

  <GenerateInvoiceModal v-if="showGenerate" @close="showGenerate = false" @created="onInvoiceGenerated" />
</template>
